// =============================================================================
// 4. TestAssertionsButton.js
// src/components/RPA/buttons/TestAssertionsButton.js
// =============================================================================
import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

export default function TestAssertionsButton({ docId, onDone, disabledHint }) {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [assertions, setAssertions] = useState({
    existence: true,
    completeness: true,
    valuation: true,
    rights_obligations: true,
    presentation: true
  });

  const handleClick = () => {
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!docId) return;
    
    const selectedAssertions = Object.keys(assertions).filter(key => assertions[key]);
    
    if (selectedAssertions.length === 0) {
      alert('Please select at least one assertion to test');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/test/assertions`,
        { assertions: selectedAssertions },
        { withCredentials: true }
      );
      
      console.log('[TEST_ASSERTIONS_BTN] Success:', res.data);
      setShowModal(false);
      if (onDone) onDone();
    } catch (err) {
      console.error('[TEST_ASSERTIONS_BTN] Failed:', err);
      alert(err.response?.data?.message || 'Assertion testing failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleAssertion = (key) => {
    setAssertions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading || !!disabledHint}
        title={disabledHint || 'Test financial statement assertions'}
        style={{
          background: loading ? '#9ca3af' : (disabledHint ? '#fca5a5' : '#3b82f6'),
          color: 'white',
          border: 'none',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 11,
          cursor: loading || disabledHint ? 'not-allowed' : 'pointer',
          fontWeight: 500
        }}
      >
        {loading ? 'Testing…' : 'Test Assertions'}
      </button>

      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'white', padding: 24, borderRadius: 12,
            maxWidth: 450, width: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 16 }}>Test Financial Assertions</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 12, color: '#6b7280' }}>
              Select assertions to test per GAAP/GAAS standards:
            </p>
            
            <div style={{ marginBottom: 16 }}>
              {[
                { key: 'existence', label: 'Existence', desc: 'Assets/liabilities exist and transactions occurred' },
                { key: 'completeness', label: 'Completeness', desc: 'All items that should be recorded are recorded' },
                { key: 'valuation', label: 'Valuation & Allocation', desc: 'Amounts are appropriate per GAAP' },
                { key: 'rights_obligations', label: 'Rights & Obligations', desc: 'Entity has legal rights to assets' },
                { key: 'presentation', label: 'Presentation & Disclosure', desc: 'Proper classification per ASC 210' }
              ].map(({ key, label, desc }) => (
                <label 
                  key={key}
                  style={{ 
                    display: 'block', 
                    marginBottom: 12, 
                    fontSize: 13,
                    padding: 8,
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: assertions[key] ? '#f0f9ff' : 'white'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={assertions[key]}
                    onChange={() => toggleAssertion(key)}
                    style={{ marginRight: 8 }}
                  />
                  <strong>{label}</strong>
                  <div style={{ fontSize: 11, color: '#6b7280', marginLeft: 20, marginTop: 2 }}>
                    {desc}
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: '1px solid #d1d5db',
                  background: 'white', cursor: 'pointer', fontSize: 13
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: loading ? '#9ca3af' : '#3b82f6', 
                  color: 'white', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13
                }}
              >
                {loading ? 'Processing…' : 'Test Selected'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}